import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { captureRef, readRef } from "@/lib/referral";
import { Check, Copy, Loader2, Mail, MessageCircle, ShieldAlert, ArrowRight, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field } from "@/components/common";
import { BrandMark } from "@/components/Brand";
import { PasswordInput } from "@/components/PasswordInput";
import { TermsText } from "@/components/TermsText";
import { validDoc } from "@/lib/document";
import { api, errMsg, maskDoc, maskPhone, digits, SEGMENT_LABELS, fmtDate } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

// Cadastro rápido (uma tela): o endereço completo é pedido antes da primeira contratação paga (tela Planos).
// CNPJ: razão social e endereço vêm da Receita quando a consulta responde; se ela falhar, o cadastro segue sem eles.
const EMPTY_ADDR = { cep: "", logradouro: "", numero: "", complemento: "", bairro: "", cidade: "", uf: "" };
const EMPTY = {
  nome_fantasia: "", razao_social: "", documento: "", segmento: "alimentacao", responsavel: "", email: "", telefone: "",
  endereco: EMPTY_ADDR, password: "", password_confirm: "", accept_terms: false, coupon: "",
};

export function validate(f) {
  if (f.nome_fantasia.trim().length < 2) return "Informe o nome do estabelecimento";
  const d = digits(f.documento);
  if (d.length !== 11 && d.length !== 14) return "Informe um CPF (11 dígitos) ou CNPJ (14 dígitos)";
  if (f.responsavel.trim().length < 2) return "Informe o nome do responsável";
  if (!/^\S+@\S+\.\S+$/.test(f.email)) return "Informe um e-mail válido";
  if (digits(f.telefone).length < 10) return "Informe um WhatsApp válido, com DDD";
  if (!SEGMENT_LABELS[f.segmento]) return "Escolha o tipo do seu negócio";
  if (f.password.length < 6) return "A senha deve ter ao menos 6 caracteres";
  if (f.password !== f.password_confirm) return "As senhas não conferem";
  if (!f.accept_terms) return "Abra e aceite os termos de uso e a política de privacidade";
  return null;
}

function Success({ data }) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const resend = async () => {
    setSending(true);
    try {
      const { data: r } = await api.post("/auth/resend-confirmation", { access_number: data.access_number, login: data.login });
      toast.success(r.message);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSending(false);
    }
  };
  const copy = () => {
    navigator.clipboard?.writeText(data.access_number);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  const msg = encodeURIComponent(
    [`*FSCsistemas · ${data.nome_fantasia}*`, "", "Seu cadastro foi feito! Confirme seu e-mail pelo link que enviamos e guarde estes dados de acesso:", "",
      `🔑 *Número de acesso:* ${data.access_number}`, `👤 *Login:* ${data.email}`, `📅 *Teste grátis até:* ${fmtDate(data.trial_end)}`, "",
      "⚠️ O número de acesso é obrigatório para entrar no sistema, junto com seu e-mail e senha.", "",
      `Acesse: ${window.location.origin}/login`].join("\n")
  );
  const waNumber = `55${digits(data.telefone).replace(/^55(?=\d{10,11}$)/, "")}`;
  return (
    <div className="space-y-6 text-center fade-up" data-testid="signup-success">
      <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-emerald-500/15 text-emerald-500"><Check className="h-7 w-7" /></span>
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold">Falta só confirmar seu e-mail</h2>
        <p className="text-muted-foreground mt-1">{data.nome_fantasia}</p>
      </div>
      <div className="card-surface p-6 space-y-3">
        <p className="eyebrow">Seu número de acesso</p>
        <p className="font-mono text-4xl sm:text-5xl font-bold tracking-[0.2em] text-primary" data-testid="access-number-badge">{data.access_number}</p>
        <Button variant="outline" size="sm" onClick={copy} data-testid="copy-access-number-btn">
          {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}{copied ? "Copiado" : "Copiar número"}
        </Button>
      </div>
      <div className="flex gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-left text-sm text-amber-700 dark:text-amber-300" data-testid="access-number-warning">
        <ShieldAlert className="h-5 w-5 shrink-0" />
        <span><b>Importante:</b> para entrar no sistema use o número de acesso, o seu e-mail (<b data-testid="signup-success-login">{data.email}</b>) e a senha. Guarde o número em local seguro.</span>
      </div>
      <div className="grid gap-2 text-sm text-left">
        <p className="flex items-start gap-2" data-testid="signup-email-status">
          <Mail className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          {data.email_sent ? `Enviamos um link de confirmação para ${data.email}. Clique nele para ativar sua conta: você só entra no sistema depois de confirmar. O número de acesso também está no e-mail.`
            : `Não conseguimos enviar o e-mail para ${data.email} agora. Toque em "Reenviar e-mail" daqui a 1 minuto.`}
        </p>
        <p className="flex items-center gap-2" data-testid="signup-trial-info">
          <CalendarClock className="h-4 w-4 text-primary" />
          Teste grátis de 15 dias: {fmtDate(data.trial_start)} até {fmtDate(data.trial_end)}
        </p>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <Button variant="outline" className="h-12" onClick={resend} disabled={sending} data-testid="signup-resend-email-btn">
          {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}Reenviar e-mail
        </Button>
        <Button asChild variant="outline" className="h-12" data-testid="send-whatsapp-btn">
          <a href={`https://wa.me/${waNumber}?text=${msg}`} target="_blank" rel="noreferrer"><MessageCircle className="h-4 w-4 mr-2" />Salvar no meu WhatsApp</a>
        </Button>
        <Button className="h-12" onClick={() => navigate("/login", { state: { access_number: data.access_number, login: data.email } })} data-testid="finish-signup-btn">
          Ir para o login
        </Button>
      </div>
    </div>
  );
}

function TermsDialog({ open, onOpenChange, doc, onAccept }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col" data-testid="terms-modal">
        <DialogHeader><DialogTitle>Termos de uso e política de privacidade</DialogTitle></DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto pr-1"><TermsText doc={doc} /></div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="terms-close-btn">Fechar</Button>
          <Button onClick={onAccept} data-testid="terms-accept-btn">Li e aceito</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const DOC_TONE = { error: "text-red-500", ok: "text-emerald-500", checking: "text-muted-foreground" };

async function runDocCheck(d) {
  if (d.length !== 11 && d.length !== 14) return { error: "Informe um CPF (11 dígitos) ou CNPJ (14 dígitos)" };
  const kind = d.length === 11 ? "CPF" : "CNPJ";
  if (!validDoc(d)) return { error: `${kind} inválido. Confira os números.` };
  try {
    const { data } = await api.get(`/auth/document-check/${d}`);
    if (data.exists) return { error: `Já existe cadastro para este ${kind}` };
  } catch {
    /* conferido de novo ao criar a conta */
  }
  if (kind === "CPF") return { ok: "CPF válido" };
  try {
    const { data } = await api.get(`/auth/cnpj/${d}`);
    return { ok: "CNPJ válido · dados da empresa conferidos na Receita", razao: data.razao_social, nome: data.nome_fantasia, addr: data.endereco };
  } catch {
    return { ok: "CNPJ válido" }; // a consulta é só uma ajuda: o endereço é pedido antes da primeira contratação
  }
}

const ADDR_KEYS = Object.keys(EMPTY_ADDR);
const fromLookup = (a) => Object.fromEntries(ADDR_KEYS.map((k) => [k, String((a || {})[k] || "")]));

export default function Signup() {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [terms, setTerms] = useState(false);
  const [termsOpened, setTermsOpened] = useState(false);
  const [termsDoc, setTermsDoc] = useState(null);
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponInfo, setCouponInfo] = useState(null);
  const [docSt, setDocSt] = useState({});
  const [params] = useSearchParams();
  const [choice] = useState(() => (params.get("modulos") || "").split(",").filter(Boolean).slice(0, 40));
  const [ref] = useState(() => { captureRef(window.location.search); return readRef(); });
  const docRun = useRef({ doc: "", p: null });
  useEffect(() => { api.get("/terms/current").then((r) => setTermsDoc(r.data?.version ? r.data : null)).catch(() => {}); }, []);
  const openTerms = () => { setTerms(true); setTermsOpened(true); };
  const acceptTerms = () => { setF((x) => ({ ...x, accept_terms: true })); setTerms(false); };
  const startDocCheck = (d) => {
    setDocSt({ status: "checking", msg: "Conferindo…" });
    const p = runDocCheck(d).then((r) => {
      if (docRun.current.doc !== d) return r.error || "";
      if (!r.error && (r.razao || r.addr)) {
        setF((x) => ({ ...x, razao_social: r.razao || "", nome_fantasia: x.nome_fantasia || r.nome || r.razao || "", endereco: fromLookup(r.addr) }));
      }
      setDocSt(r.error ? { status: "error", msg: r.error } : { status: "ok", msg: r.ok });
      return r.error || "";
    });
    docRun.current = { doc: d, p };
    return p;
  };
  const checkDoc = () => {
    const d = digits(f.documento);
    return docRun.current.doc === d && docRun.current.p ? docRun.current.p : startDocCheck(d);
  };
  const onDoc = (e) => {
    const v = maskDoc(e.target.value);
    setF((x) => ({ ...x, documento: v, razao_social: "", endereco: EMPTY_ADDR })); // outro documento: descarta os dados da consulta anterior
    docRun.current = { doc: "", p: null };
    setDocSt({});
    if (digits(v).length === 14) startDocCheck(digits(v));
  };
  const checkCoupon = async () => {
    if (!f.coupon.trim()) return setCouponInfo(null);
    try {
      const { data } = await api.get(`/coupons/check/${encodeURIComponent(f.coupon.trim())}`);
      setCouponInfo({ ok: true, text: `Cupom válido: +${data.trial_days} dias de teste grátis` });
    } catch (e) {
      setCouponInfo({ ok: false, text: errMsg(e) });
    }
  };
  const set = (k, mask) => (e) => setF((x) => ({ ...x, [k]: mask ? mask(e.target.value) : e.target.value }));

  const submit = async () => {
    const err = validate(f);
    if (err) return toast.error(err);
    const docErr = await checkDoc();
    if (docErr) return toast.error(docErr);
    setBusy(true);
    try {
      const end = f.endereco;
      const temEndereco = ADDR_KEYS.some((k) => end[k]);
      const { endereco: _e, ...rest } = f;
      const { data } = await api.post("/auth/signup", {
        ...rest, modules: choice, ref, terms_version: termsDoc?.version || "", documento: digits(f.documento), telefone: digits(f.telefone),
        ...(temEndereco ? { endereco: { ...end, cep: digits(end.cep), uf: (end.uf || "").toUpperCase().slice(0, 2) } } : {}),
      });
      setDone(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grain flex items-start sm:items-center justify-center p-4 sm:p-8">
      <div className="w-full max-w-2xl">
        <div className="flex items-center justify-between mb-6">
          <Link to="/login" className="flex items-center gap-3">
            <BrandMark tone="bg-primary text-primary-foreground" testId="signup-brand" />
            <span className="font-display text-lg font-bold">FSCsistemas</span>
          </Link>
          <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground" data-testid="signup-go-login">Já tenho conta</Link>
        </div>
        <div className="card-surface p-6 sm:p-8">
          {done ? <Success data={done} /> : (
            <>
              <p className="eyebrow mb-2">15 dias grátis · sem cartão</p>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Cadastre seu estabelecimento</h1>
              <p className="mt-1 text-sm text-muted-foreground">Leva 1 minuto. O endereço só é pedido quando você for contratar um plano.</p>
              {choice.length > 0 && <p className="mt-3 rounded-lg bg-primary/10 px-3 py-2 text-sm" data-testid="signup-plan-choice">Plano personalizado com {choice.length} módulo(s). No teste grátis tudo fica liberado; depois você assina só os escolhidos na tela Planos.</p>}
              <form className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="signup-form" onSubmit={(e) => { e.preventDefault(); submit(); }}>
                <Field label="Nome do estabelecimento" className="sm:col-span-2"><Input value={f.nome_fantasia} onChange={set("nome_fantasia")} autoComplete="organization" data-testid="signup-nome-fantasia-input" /></Field>
                <Field label="CPF ou CNPJ" hint={docSt.msg && <span className={DOC_TONE[docSt.status]} data-testid="signup-documento-status">{docSt.msg}</span>}>
                  <Input value={f.documento} onChange={onDoc} onBlur={() => digits(f.documento) && checkDoc()} inputMode="numeric" className={cn(docSt.status === "error" && "border-red-500 focus-visible:ring-red-500")} data-testid="signup-documento-input" />
                </Field>
                <Field label="Nome do responsável"><Input value={f.responsavel} onChange={set("responsavel")} autoComplete="name" data-testid="signup-responsavel-input" /></Field>
                <Field label="E-mail" hint="Será o seu login"><Input type="email" value={f.email} onChange={set("email")} autoComplete="email" data-testid="signup-email-input" /></Field>
                <Field label="WhatsApp"><Input value={f.telefone} onChange={set("telefone", maskPhone)} inputMode="tel" autoComplete="tel" data-testid="signup-telefone-input" /></Field>
                <div className="sm:col-span-2 space-y-2">
                  <p className="text-sm font-medium">Tipo do negócio</p>
                  <div className="flex flex-wrap gap-2" role="radiogroup" data-testid="signup-segmento-group">
                    {Object.entries(SEGMENT_LABELS).map(([k, v]) => (
                      <button key={k} type="button" role="radio" aria-checked={f.segmento === k} onClick={() => setF((x) => ({ ...x, segmento: k }))}
                        className={cn("rounded-full border px-3 py-1.5 text-sm transition-colors", f.segmento === k ? "border-primary bg-primary text-primary-foreground" : "hover:bg-muted")}
                        data-testid={`signup-segmento-${k}`}>{v}</button>
                    ))}
                  </div>
                </div>
                <Field label="Senha" hint="Mínimo 6 caracteres"><PasswordInput value={f.password} onChange={set("password")} autoComplete="new-password" testId="signup-password-input" /></Field>
                <Field label="Confirmação de senha"><PasswordInput value={f.password_confirm} onChange={set("password_confirm")} autoComplete="new-password" testId="signup-password-confirm-input" /></Field>
                {couponOpen ? (
                  <Field label="Cupom" className="sm:col-span-2" hint={couponInfo ? <span className={couponInfo.ok ? "text-emerald-500" : "text-red-500"} data-testid="signup-coupon-status">{couponInfo.text}</span> : "O cupom aumenta seu período de teste."}>
                    <div className="flex gap-2"><Input value={f.coupon} onChange={(e) => { setF((x) => ({ ...x, coupon: e.target.value.toUpperCase() })); setCouponInfo(null); }} onBlur={checkCoupon} data-testid="signup-coupon-input" /><Button type="button" variant="outline" onClick={checkCoupon} data-testid="signup-coupon-check-btn">Aplicar</Button></div>
                  </Field>
                ) : (
                  <button type="button" className="sm:col-span-2 justify-self-start text-sm text-primary underline" onClick={() => setCouponOpen(true)} data-testid="signup-coupon-toggle">Tenho um cupom</button>
                )}
                <div className="sm:col-span-2 flex items-start gap-3 text-sm">
                  <Checkbox checked={f.accept_terms} onCheckedChange={(v) => (termsOpened ? setF((x) => ({ ...x, accept_terms: !!v })) : openTerms())} className="mt-0.5" data-testid="signup-terms-checkbox" />
                  <span>Li e aceito os <button type="button" onClick={openTerms} className="text-primary underline" data-testid="signup-terms-link">termos de uso e a política de privacidade</button>.
                    {!termsOpened && <span className="block text-xs text-muted-foreground" data-testid="signup-terms-hint">Abra os termos para poder aceitar.</span>}
                  </span>
                </div>
                <Button type="submit" disabled={busy} className="sm:col-span-2 h-12 text-base" data-testid="signup-submit-btn">
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Criar conta e começar o teste<ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
      <TermsDialog open={terms} onOpenChange={setTerms} doc={termsDoc} onAccept={acceptTerms} />
    </div>
  );
}
